/**
 * Module-level access-token store. IN-MEMORY ONLY — never persisted to
 * `localStorage`, `sessionStorage`, or any cookie (Option A invariant). The
 * decoded JWT payload is cached alongside the token so consumers don't decode
 * on every read.
 *
 * The CSRF token lives here too (H §4.2, `store-token` mode): saas-auth's
 * `journix_csrf_r<productId>` cookie lives on the auth host and is unreadable from
 * this origin, so the token comes from `GET /api/auth/csrf` and every refresh
 * response. It is NEVER persisted and is cleared on sign-out.
 *
 * **Session epoch.** A sign-out bumps the epoch; a refresh started before it can
 * no longer store a token (`setAccessTokenIfCurrent` / `setCsrfTokenIfCurrent`).
 *
 * **Proactive refresh (H §4.2).** When a token is stored and a handler is
 * registered (`registerProactiveRefresh`, done by `AuthProvider`), ONE timer is
 * armed to fire ~60 s before expiry. Storing a new token re-arms it; sign-out
 * clears it. The expiry is the one supplied by the caller, else the token's own
 * `exp` claim — the factory's reactive refresh forwards no expiry, and the
 * saas-auth refresh body carries `expiresIn`, not an instant.
 */
import { decodeJwtPayload, type JwtPayload } from '@/utils/jwt';

/**
 * Why a session ended without the operator asking: `expired` (a bearer `401` that
 * could not be refreshed), `revoked` (`RESELLER_ACCESS_REVOKED`), `misconfigured`
 * (`REFRESH_ORIGIN_UNROUTABLE` / `INVALID_ORIGIN`). `null` is a deliberate sign-out.
 */
export type SessionEndReason = 'expired' | 'revoked' | 'misconfigured';

interface StoreState {
  token: string | null;
  expiresAt: string | null;
  claims: JwtPayload | null;
  csrfToken: string | null;
}

const state: StoreState = {
  token: null,
  expiresAt: null,
  claims: null,
  csrfToken: null,
};

/** Refresh this long before expiry. */
const PROACTIVE_LEAD_MS = 60_000;
/** Never re-fire faster than this, whatever the expiry says — no tight loops. */
const PROACTIVE_FLOOR_MS = 5_000;
/** `setTimeout` overflows past 2^31-1 ms and would fire immediately. */
const MAX_TIMER_MS = 2_147_483_647;

let signOutCallback: ((reason: SessionEndReason | null) => void) | null = null;
let proactiveRefresh: (() => void) | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let sessionEpoch = 0;

export function getSessionEpoch(): number {
  return sessionEpoch;
}

export function getAccessToken(): string | null {
  return state.token;
}

export function getClaims(): JwtPayload | null {
  return state.claims;
}

export function getExpiresAt(): string | null {
  return state.expiresAt;
}

export function setAccessToken(token: string | null, expiresAt: string | null = null): void {
  state.token = token;
  state.claims = token ? decodeJwtPayload(token) : null;
  state.expiresAt = token ? (expiresAt ?? expiryFromClaims(state.claims)) : null;
  armProactiveRefresh();
}

export function getCsrfToken(): string | null {
  return state.csrfToken;
}

export function setCsrfToken(token: string | null): void {
  state.csrfToken = token;
}

/** Store a token only if no sign-out happened since `epoch` was read. Returns whether it stored. */
export function setAccessTokenIfCurrent(
  epoch: number,
  token: string | null,
  expiresAt: string | null = null,
): boolean {
  if (epoch !== sessionEpoch) return false;
  setAccessToken(token, expiresAt);
  return true;
}

export function setCsrfTokenIfCurrent(epoch: number, token: string | null): void {
  if (epoch !== sessionEpoch) return;
  setCsrfToken(token);
}

export function registerSignOutCallback(
  cb: ((reason: SessionEndReason | null) => void) | null,
): void {
  signOutCallback = cb;
}

/**
 * Register the proactive-refresh handler (or `null` to remove it). Arms the timer
 * for the current token, if any.
 */
export function registerProactiveRefresh(fn: (() => void) | null): void {
  proactiveRefresh = fn;
  armProactiveRefresh();
}

/**
 * Clear all in-memory auth state, disarm the proactive timer, and fire the
 * sign-out callback. `opts.reason` says why a session ended on its own (see
 * `SessionEndReason`) so React can show the matching screen; omitted = a
 * deliberate sign-out.
 */
export function signOut(opts?: { reason?: SessionEndReason }): void {
  sessionEpoch += 1;
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  disarmProactiveRefresh();
  signOutCallback?.(opts?.reason ?? null);
}

function expiryFromClaims(claims: JwtPayload | null): string | null {
  const exp = claims?.exp;
  return typeof exp === 'number' && Number.isFinite(exp)
    ? new Date(exp * 1000).toISOString()
    : null;
}

function disarmProactiveRefresh(): void {
  if (proactiveTimer !== null) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

function armProactiveRefresh(): void {
  disarmProactiveRefresh();
  if (!state.token || !state.expiresAt || !proactiveRefresh) return;
  const expiresMs = Date.parse(state.expiresAt);
  if (!Number.isFinite(expiresMs)) return;
  const delay = Math.min(
    Math.max(expiresMs - Date.now() - PROACTIVE_LEAD_MS, PROACTIVE_FLOOR_MS),
    MAX_TIMER_MS,
  );
  const handler = proactiveRefresh;
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    handler();
  }, delay);
}

/** Test seam — resets all in-memory state, the timer and the registered callbacks. */
export function __resetAuthStore(): void {
  sessionEpoch += 1;
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  disarmProactiveRefresh();
  signOutCallback = null;
  proactiveRefresh = null;
}
